import { Alert, Box, Stack } from "@mui/material";
import { GridSortDirection, GridSortModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomDataGrid, DeletePanel, MyPaper, TitleHeader } from "../../../components";
import { CurrentView } from "../../../components/boardLayout/components/currentView";
import { useUserPreference } from "../../../hooks/useUserPreference";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeleteProposalMutation, useLazyGetProposalsQuery } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateConfirmationDialogPositiveBtn, updateCongirmationDialogBody } from "../../../redux/slices/confirmationDialog";
import { resetProposalSlice } from "../../../redux/slices/proposal";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { AppRoutes } from "../../../router/routes";
import { TABLE_COLUMN_PREFERENCES } from "../../viewSettings/utils";
import { DashboardTableColumn } from "../utils";

export const Dashboard: React.FC<{ children?: JSX.Element }> = (props) => {
    const apiRef = useGridApiRef();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [getAllProposals, { data: allData = { data: [], totalCount: 0 }, isFetching, isLoading, isSuccess, isError }] = useLazyGetProposalsQuery({ refetchOnFocus: true });

    const { enqueueSnackbar } = useSnackbar();
    const [deleteProposalMutation] = useDeleteProposalMutation();
    const { userDetails, userPreferences } = useAppSelector(store => store.auth);
    const { perm } = userDetails;

    const proposal_table_config = React.useMemo(() => JSON.parse(userPreferences.find((pref) => pref.key === TABLE_COLUMN_PREFERENCES.proposal_table_columns)?.value || "{}"), [userPreferences]);

    const [search, setSearch] = React.useState("");
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 100 });
    const [queryOptions, setQueryOptions] = React.useState({ sortModel: [{ field: 'sequence_id', sort: 'desc' as GridSortDirection }] });

    const { data, totalCount } = allData;

    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.PROPOSAL_CREATE), [perm]);
    const deletable = React.useMemo(() => HasAccess(perm, PERMISSIONS.PROPOSAL_DELETE), [perm]);

    const saveProposalPreference = useUserPreference();

    const addEditProposal = (data: any, isEdit: boolean = true) => {
        dispatch(resetProposalSlice())
        navigate(AppRoutes.viewProposalBasicDetail(isEdit ? data._id : "New"))
    };

    const getPath = (id: string) => AppRoutes.viewProposalBasicDetail(id);

    const refetch = () => {
        getAllProposals({
            ...paginationModel,
            sort_by: queryOptions?.sortModel?.[0]?.field,
            sort_order: queryOptions?.sortModel?.[0]?.sort,
            search: search.trim() ? search : ""
        });
    }

    const deleteProposal = (proposal: any) => {
        let message = `Check for dependencies before deleting the Proposal ${proposal.sequence_id}`;
        dispatch(openConfirmationDialog({
            title: "Delete Operation",
            body: <DeletePanel
                message={message}
                warningMessage={""}
            />,
            positiveBtn: "Check for dependencies",
            negativeBtn: "Cancel",
            onOk: () => openDependency(proposal),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const openDependency = (proposal: any) => {
        dispatch(updateCongirmationDialogBody(<DeletePanel
            message={proposal.associations ? proposal.associations.length === 0 ? 'No dependency found' : `Dependency Table of ${proposal?.sequence_id}` : "No dependency found"}
            errorMessage={""}
            errorMessageStacks={proposal?.associations ?? []}
            warningMessage={""}
        />))
        dispatch(updateConfirmationDialogPositiveBtn({
            positiveBtn: "Force Delete",
            onOk: () => {
                dispatch(updateCongirmationDialogBody(<DeletePanel
                    message={"Are you sure want to force delete the Proposal " + proposal?.sequence_id + "?"}
                    warningMessage={"Warning: All the proposal contents and its associated entities will be de-linked"}
                />))
                dispatch(updateConfirmationDialogPositiveBtn({
                    positiveBtn: "Ok",
                    onOk: () => performRowDel(proposal, true)
                }))
            },
        }))
    };

    const performRowDel = async (proposal: any, force: boolean = false) => {
        dispatch(openBackdrop("Deleting proposal..."));
        let res: any = {};
        try {
            res = await deleteProposalMutation({ id: proposal._id, force })

            if (Object.keys(res).includes("data")) {
                enqueueSnackbar(`Deleted proposal Successfully!`, { variant: "success" })
                dispatch(closeConfirmationDialog());
                refetch();
            } else if (Object.keys(res).includes("error")) {
                // eslint-disable-next-line 
                throw res.error.data;
            } else {
                // eslint-disable-next-line 
                throw "Data not found"
            }
        } catch (error: any) {
            let errorMessage: string = error?.title ?? "Opps! Something went wrong, Unable to delete"
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            dispatch(closeBackdrop());
        }
    };

    const giveMeRows = () => {
        if (!isLoading && !isFetching && data) {
            return data ?? [];
        } else {
            return [];
        }
    };

    let ROWS = giveMeRows();

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        // Here you save the data you need from the sort model
        setQueryOptions({ sortModel: [...sortModel] });
    }, []);

    React.useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, [paginationModel, JSON.stringify(queryOptions?.sortModel)]);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            refetch();
        }, 500)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);
    
    React.useEffect(() => {
        if (proposal_table_config) {
            apiRef?.current?.restoreState(proposal_table_config);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proposal_table_config, apiRef?.current, isSuccess]);    

    return (
        <MyPaper>
            <>
                <Stack gap={1} sx={{ flexDirection: { sm: "column", md: "row" } }} width={"100%"} alignItems="center">
                    <CurrentView entity={"proposal"} />

                    <Stack flexGrow={1}>
                        {/* Header */}
                        <TitleHeader
                            title="Proposals"
                            count={ROWS?.length ?? 0}
                            showSearch={true}
                            search={search}
                            onSearchChange={setSearch}
                            searchPlaceholder={"Search for Proposals"}
                            showCreateBtn={creatable}
                            createBtnLabel={"Create Proposal"}
                            onCreateBtn={() => addEditProposal(null, false)}
                        />
                    </Stack>
                </Stack>

                {/* Error */}
                {isError && <Alert sx={{ mt: 2 }} severity="error">Oops! Something went wrong, Unable to fetch Proposals. Try Again Later!</Alert>}


                {/* Data Grid */}
                <Box
                    sx={{
                        mt: 2,
                        height: "calc(100vh - 216px)"
                    }}>
                    <CustomDataGrid
                        apiRef={apiRef}
                        id="proposal-list"
                        saveLocal={false}
                        rows={ROWS}
                        columns={DashboardTableColumn(deleteProposal, addEditProposal, getPath, proposal_table_config?.columns?.dimensions, proposal_table_config?.columns?.orderedFields || [], !deletable)}
                        loading={isLoading || isFetching}
                        getRowId={(row) => row._id}
                        showToolbar={true}
                        rowCount={totalCount}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        onPaginationModelChange={setPaginationModel}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        preferenceDetails={{
                            include: true,
                            onPreferenceSave: () => saveProposalPreference({
                                key: TABLE_COLUMN_PREFERENCES.proposal_table_columns,
                                value: JSON.stringify(apiRef?.current?.exportState())
                            }),
                        }}
                    />
                </Box>
            </>
        </MyPaper>
    );
}